import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { toast } from 'react-toastify'

import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import { errorText } from '../../../../shared/errorMessage'
import { ANFRAGE_GESENDET } from '../../../../shared/requestEvents'
import MiniHeader from '../../../../UI/MiniHeader'
import Loading from '../../../../UI/Loading'

import RequestDetail from './RequestDetail'
import styles from './style.module.scss'

/**
 * „Meine Anfragen" — der Rückweg für Schüler:innen, Lehrkräfte und
 * Unterstützer:innen (Anfragen-Konzept, Stufe 3).
 *
 * Bewusst eine **eigene Seite** und nicht nur ein Abschnitt in den
 * Mitteilungen: Eine Mitteilung ist ein Ereignis und rutscht nach unten weg;
 * eine Anfrage ist ein Vorgang, den man in drei Wochen wiederfinden können
 * muss. Die Mitteilungen führen hierher, nicht umgekehrt.
 *
 * Was hier steht, kommt aus einer eigenen Schnittstelle, die nur die eigenen
 * Vorgänge kennt — und von jedem Vorgang nur, was hinaus darf. Interne
 * Vermerke der Verwaltung gibt es hier nicht.
 */
export default function MyRequests() {
	const { t } = useTranslation()
	const location = useLocation()
	const darfAnfragen = useSelector(state => state.user.profile.mayCreateRequest !== false)

	const [rows, setRows] = useState(null)
	const [offen, setOffen] = useState(null)

	// Die Übersetzungsfunktion über eine Referenz: `useTranslation()` gibt bei
	// jedem Render eine neue, und in einer Abhängigkeitsliste liefe das Laden in
	// die Schleife Render → Laden → Render (shared/translationDeps.test).
	const uebersetze = useRef(t)
	uebersetze.current = t

	const laden = useCallback(async () => {
		try {
			const { data } = await http.get(TApi.REQUESTS, { disableToast: true })
			setRows(data.rows || [])
		} catch (error) {
			setRows([])
			toast.error(errorText(error, uebersetze.current('Your requests could not be loaded.')))
		}
	}, [])

	/*
	 * Neu geholt wird bei jedem Aufruf der Seite — auch, wenn er auf **dieselbe**
	 * Adresse führt. Genau das war der Fall, der schiefging: Wer den Hilfe-Knopf
	 * drückte, während diese Seite offen war, und danach dem Verweis in der
	 * Bestätigung folgte, blieb auf der Liste sitzen, die vor dem Absenden
	 * geholt worden war — samt „Sie haben noch keine Anfrage gestellt".
	 * `location.key` unterscheidet zwei Aufrufe derselben Adresse.
	 */
	useEffect(() => {
		laden()
	}, [laden, location.key])

	/*
	 * Und ohne jeden Aufruf: Wer das Bestätigungsfenster einfach schließt, sieht
	 * die Liste, die dahinter liegt — die muss die eben gestellte Anfrage schon
	 * enthalten.
	 */
	useEffect(() => {
		const beiNeuerAnfrage = () => laden()
		window.addEventListener(ANFRAGE_GESENDET, beiNeuerAnfrage)
		return () => window.removeEventListener(ANFRAGE_GESENDET, beiNeuerAnfrage)
	}, [laden])

	/*
	 * Aus einer Mitteilung kommt man mit `?nr=123` hierher — dann geht der
	 * Vorgang gleich auf. Ohne das landete man in einer Liste und müsste die
	 * Nummer suchen, die in der Mitteilung stand.
	 */
	useEffect(() => {
		const nummer = Number(new URLSearchParams(location.search).get('nr'))
		if (nummer) setOffen(nummer)
	}, [location.search])

	if (rows === null) return <Loading />

	return (
		<div className={styles.root}>
			<MiniHeader title={t('My requests')} bg="#f8faff" />

			<div className={styles.body}>
				{/*
				 * Der leere Zustand verwies bis hierher unbedingt auf den
				 * Hilfe-Knopf. Seit sich der Kanal je Rolle und Unternehmensform
				 * abschalten lässt (Einstellungen → Anfragen), gibt es den Knopf
				 * nicht überall — und ein Verweis auf etwas, das nicht da ist,
				 * ist schlimmer als gar keiner.
				 */}
				{!rows.length && (
					<p className={styles.empty}>
						{darfAnfragen
							? t('You have not submitted any requests yet. Use the help button at the bottom right.')
							: t('You have not submitted any requests yet.')}
					</p>
				)}

				{rows.map(row => (
					<button
						type="button"
						key={row.id}
						className={clsx(styles.card, row.awaitsMyAnswer && styles.cardAction)}
						onClick={() => setOffen(row.id)}
					>
						<span className={styles.number}>#{row.id}</span>
						<span className={styles.title}>{row.title}</span>
						<span
							className={clsx(
								styles.status,
								row.awaitsMyAnswer && styles.statusAction,
								row.canConfirm && styles.statusReview,
								row.closed && styles.statusClosed
							)}
						>
							{row.status}
						</span>
					</button>
				))}
			</div>

			{offen !== null && (
				<RequestDetail
					id={offen}
					onClose={() => setOffen(null)}
					onChanged={laden}
				/>
			)}
		</div>
	)
}
